import { getAuthHeaders, getToken } from "../lib/auth";
import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, CheckCircle2, FileText, AlertTriangle, Info, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const NOTIF_ICONS = {
  invoice_status: FileText,
  alert: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

const NOTIF_COLORS = {
  invoice_status: "text-sky-400",
  alert: "text-amber-400",
  success: "text-emerald-400",
  info: "text-slate-400",
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Determine redirect link from notification metadata
  const getNotifLink = (n) => {
    if (n.link) return n.link;
    const t = (n.type || "").toLowerCase();
    const meta = n.metadata || n.data || {};
    if (t.includes("invoice") || t.includes("facture")) return "/log/invoices";
    if (t.includes("task") || t.includes("tache")) return "/log/taches";
    if (t.includes("course") || t.includes("expedition")) return "/log";
    if (t.includes("subscription") || t.includes("abonnement")) return "/log/abonnement";
    if (t.includes("alert") || t.includes("creance")) return "/log/creances";
    if (meta.invoice_id) return "/log/invoices";
    if (meta.task_id) return "/log/taches";
    return null;
  };

  const handleNotifClick = async (n) => {
    if (n.id) {
      try {
        await axios.put(`${API}/subscriptions/notifications/read/${n.id}`, {}, { headers: getHeaders() });
      } catch { /* silent */ }
    }
    setNotifications(prev => prev.filter(x => x.id !== n.id));
    setUnreadCount(prev => Math.max(0, prev - 1));
    window.dispatchEvent(new Event("cmes-notif-read"));
    setShowPanel(false);
    const link = getNotifLink(n);
    if (link) navigate(link);
  };

  // Load unread notifications
  const loadUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/notifications/unread`, { headers: getHeaders() });
      const notifs = (res.data.notifications || []).filter(n => n.type !== "ping");
      setNotifications(notifs);
      setUnreadCount(notifs.length);
    } catch { /* silent */ }
  }, []);

  // Connect to SSE stream
  useEffect(() => {
    const token = getToken();
    if (!token) return;

    loadUnread();

    const url = `${API}/subscriptions/notifications/stream?token=${token}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "ping") return;
        setNotifications(prev => [data, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        // Show browser notification if permission granted
        if (Notification.permission === "granted") {
          new Notification(data.title || "Pharma Intel", { body: data.message || "", icon: "/logo192.png" });
        }
      } catch { /* silent */ }
    };

    es.onerror = () => {
      es.close();
      setTimeout(() => loadUnread(), 5000);
    };

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
  }, [loadUnread]);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setShowPanel(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/subscriptions/notifications/read/${id}`, {}, { headers: getHeaders() });
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
      window.dispatchEvent(new Event("cmes-notif-read"));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/subscriptions/notifications/mark-all-read`, {}, { headers: getHeaders() });
      setNotifications([]);
      setUnreadCount(0);
      window.dispatchEvent(new Event("cmes-notif-read"));
    } catch { /* silent */ }
  };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return "A l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return `Il y a ${Math.floor(diff / 86400)}j`;
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell">
      <button onClick={() => { setShowPanel(!showPanel); if (!showPanel) loadUnread(); }}
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors" data-testid="bell-button">
        <Bell className={`w-5 h-5 ${unreadCount > 0 ? "text-amber-400" : "text-slate-500"}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="unread-count">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <div className="absolute right-0 top-12 w-80 max-h-96 bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between p-3 border-b border-white/5">
            <h3 className="text-sm font-bold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <button onClick={markAllRead} data-testid="mark-all-read-btn"
                  className="text-[10px] text-sky-400 hover:text-sky-300 transition-colors font-medium">
                  Tout marquer lu
                </button>
              )}
              <button onClick={() => setShowPanel(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-500">Aucune notification</div>
            ) : notifications.map((n, i) => {
              const Icon = NOTIF_ICONS[n.type] || Info;
              const color = NOTIF_COLORS[n.type] || "text-slate-400";
              const link = getNotifLink(n);
              return (
                <div key={n.id || i}
                  onClick={() => handleNotifClick(n)}
                  className={`flex items-start gap-3 p-3 border-b border-white/5 hover:bg-white/5 transition-colors group ${link ? "cursor-pointer" : ""}`}
                  data-testid={`notif-${n.id || i}`}>
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{n.title}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{n.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-[9px] text-slate-600">{timeAgo(n.created_at)}</p>
                      {link && <span className="text-[9px] text-sky-500 flex items-center gap-0.5"><ExternalLink className="w-2.5 h-2.5" /> Voir</span>}
                    </div>
                  </div>
                  {n.id && (
                    <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-emerald-400 transition-all p-1" title="Marquer comme lu">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
